import React, { useState, useMemo, useRef, useEffect } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import StarIcon from "@mui/icons-material/Star";
import SearchIcon from "@mui/icons-material/Search";
import HotelIcon from "@mui/icons-material/Hotel";
import Slider from "@mui/material/Slider";
import styled from "styled-components";

const HotelSearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const HotelNameInput = styled.input`
  border-radius: 12px;
  padding: 12px 16px 12px 45px;
  border: 1px solid #003663;
  color: #003663;
  font-size: 0.95rem;
  background-color: #fff;
  width: 100%;
  transition: all 0.2s ease-in-out;
  outline: none;
  font-weight: 500;
  font-family: inherit;

  &::placeholder {
    color: #003663;
    opacity: 0.7;
    font-weight: 400;
  }

  &:hover {
    border-color: #003663;
    box-shadow: 0 2px 4px rgba(0, 54, 99, 0.1);
  }

  &:focus {
    border: 2px solid #003663;
    box-shadow: 0 0 0 2px rgba(0, 54, 99, 0.1);
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #003663;
  opacity: 0.7;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 20px;
  }
`;

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 54, 99, 0.12);
  max-height: 320px;
  overflow-y: auto;
  z-index: 1000;
  padding: 8px 0;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #003663;
    border-radius: 8px;
    opacity: 0.7;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #002845;
  }
`;

const SuggestionItem = styled.div`
  padding: 0 16px;
  cursor: pointer;
  height: 60px;
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #f8f9fa;
  transition: all 0.2s ease;
  border-bottom: 1px solid #eef1f5;

  &:hover {
    background-color: #003663;
    
    .hotel-icon {
      color: white;
    }

    .hotel-name {
      color: white;
    }

    .hotel-location {
      color: rgba(255, 255, 255, 0.7);
    }
  }

  &:last-child {
    border-bottom: none;
  }

  .hotel-icon {
    color: #003663;
    flex-shrink: 0;
    font-size: 24px;
    transition: color 0.2s ease;
  }

  .hotel-name {
    font-weight: 500;
    flex: 1;
    font-family: inherit;
    color: #003663;
    transition: color 0.2s ease;
  }

  .hotel-location {
    font-size: 0.85rem;
    color: #666;
    margin-top: 2px;
    transition: color 0.2s ease;
  }
`;

// Custom sleeping SVG component
const SleepingSVG = ({ style, className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 50 50" 
    style={style}
    className={className}
  >
    <path d="M4.921 27.836V12.194a1.96 1.96 0 1 0-3.921 0v25.612a1.96 1.96 0 0 0 3.921 0v-3.673l40.158-.095v3.674a1.96 1.96 0 1 0 3.921 0V27.741z" fill="currentColor" />
    <path d="M49 26.157H19.196v-7.388a3.875 3.875 0 0 1 3.875-3.875h18.106A7.823 7.823 0 0 1 49 22.717z" fill="currentColor" />
    <circle cx="12.219" cy="20.526" r="4.787" fill="currentColor" />
  </svg>
);

function FiltersSidebar({ allHotels, filters, onChangeFilters, isMobile, onCloseDrawer, translation }) {
  const [showAllFacilities, setShowAllFacilities] = useState(false);
  const [showAllThemes, setShowAllThemes] = useState(false);
  const [selectedStarRatings, setSelectedStarRatings] = useState(filters.starRating || []);
  const [priceRange, setPriceRange] = useState([
    filters.priceMin || 0,
    filters.priceMax || Math.max(...allHotels.map(h => h.displayPrice || 0))
  ]);
  
  // Hotel search autocomplete state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.hotelName || "");
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Common text color style
  const textColorStyle = { color: "#003663" };

  // Mobile-specific CSS overrides
  useEffect(() => {
    if (isMobile) {
      const style = document.createElement('style');
      style.id = 'mobile-filter-styles';
      style.textContent = `
        /* Mobile filter container fixes */
        .mobile-filter-container {
          padding: 16px !important;
          background: white !important;
          width: 100% !important;
          box-sizing: border-box !important;
        }
        
        .mobile-filter-container h5 {
          color: #003663 !important;
          font-size: 1.1rem !important;
          font-weight: 600 !important;
          margin: 16px 0 12px 0 !important;
        }
        
        .mobile-filter-item {
          display: flex !important;
          align-items: center !important;
          padding: 12px 16px !important;
          margin-bottom: 8px !important;
          border: 1px solid #E5E7EB !important;
          border-radius: 8px !important;
          background: white !important;
          width: 100% !important;
          box-sizing: border-box !important;
          gap: 12px !important;
          min-height: 48px !important;
        }
        
        .mobile-filter-item input {
          width: 20px !important;
          height: 20px !important;
          margin: 0 !important;
          flex-shrink: 0 !important;
        }
        
        .mobile-filter-item label {
          flex: 1 !important;
          color: #003663 !important;
          font-size: 0.95rem !important;
          font-weight: 500 !important;
          margin: 0 !important;
          line-height: 1.4 !important;
          cursor: pointer !important;
        }
        
        .mobile-filter-item .count-badge {
          background: #f0f0f0 !important;
          color: #666 !important;
          padding: 4px 8px !important;
          border-radius: 12px !important;
          font-size: 0.8rem !important;
          min-width: 30px !important;
          text-align: center !important;
          flex-shrink: 0 !important;
        }
        
        .mobile-star-container {
          display: flex !important;
          align-items: center !important;
          gap: 4px !important;
        }
        
        .mobile-star-icon {
          color: #FFC107 !important;
          font-size: 16px !important;
        }
        
        .mobile-clear-button {
          width: 100% !important;
          padding: 12px !important;
          background: #FBD215 !important;
          color: #003663 !important;
          border: none !important;
          border-radius: 8px !important;
          font-weight: 600 !important;
          font-size: 1rem !important;
          margin-top: 20px !important;
        }
      `;
      
      // Remove existing style if it exists
      const existingStyle = document.getElementById('mobile-filter-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
      
      document.head.appendChild(style);
      
      return () => {
        const styleToRemove = document.getElementById('mobile-filter-styles');
        if (styleToRemove) {
          styleToRemove.remove();
        }
      };
    }
  }, [isMobile]);

  // Helper function to translate facility names
  const translateFacilityName = (facilityName) => {
    if (!facilityName) return facilityName;
    
    const normalizedName = facilityName.toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[()]/g, '')
      .replace(/\//g, '_')
      .replace(/-/g, '_');
    
    const translationKey = `facility_${normalizedName}`;
    const translatedText = translation?.[translationKey] || translation(translationKey);
    
    return translatedText !== translationKey ? translatedText : facilityName;
  };

  // Helper function to translate theme names
  const translateThemeName = (themeName) => {
    if (!themeName) return themeName;
    
    const normalizedName = themeName.toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[()]/g, '')
      .replace(/-/g, '_');
    
    const translationKey = `theme_${normalizedName}`;
    const translatedText = translation?.[translationKey] || translation(translationKey);
    
    return translatedText !== translationKey ? translatedText : themeName;
  };

  // Helper function to translate board/meal plan names
  const translateBoardName = (boardName) => {
    if (!boardName) return boardName;
    
    const normalizedName = boardName.toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[()]/g, '')
      .replace(/&/g, 'and')
      .replace(/-/g, '_');
    
    const translationKey = `board_${normalizedName}`;
    const translatedText = translation?.[translationKey] || translation(translationKey);
    
    return translatedText !== translationKey ? translatedText : boardName;
  };

  // Helper function to check if a hotel matches all current filters except the one being counted
  const matchesCurrentFilters = (hotel, excludeFilter) => {
    // Hotel name filter
    if (excludeFilter !== 'hotelName' && filters.hotelName && 
        !hotel.name.toLowerCase().includes(filters.hotelName.toLowerCase())) {
      return false;
    }

    // Price range filter
    if (excludeFilter !== 'price') {
      if (filters.priceMin != null && hotel.displayPrice < filters.priceMin) return false;
      if (filters.priceMax != null && hotel.displayPrice > filters.priceMax) return false;
    }

    // Distance filter (multi)
    if (excludeFilter !== 'distance' && Array.isArray(filters.distanceKm) && filters.distanceKm.length > 0) {
      const maxAllowed = Math.min(...filters.distanceKm);
      if (hotel.distanceFromCenter > maxAllowed) return false;
    }

    // Star rating filter
    if (excludeFilter !== 'starRating' && filters.starRating?.length > 0 && 
        !filters.starRating.some(r => Math.round(hotel.stars || 0) >= r)) {
      return false;
    }

    // Meal plan filter (multi)
    if (excludeFilter !== 'mealPlan' && Array.isArray(filters.mealPlan) && filters.mealPlan.length > 0) {
      if (!hotel.boardGroups?.some(bg => filters.mealPlan.includes(bg.name))) {
        return false;
      }
    }

    // Facilities filter
    if (excludeFilter !== 'facilities' && filters.facilities?.length > 0 && 
        !filters.facilities.every(f => hotel.facilities?.map(x => x.name).includes(f))) {
      return false;
    }

    // Themes filter
    if (excludeFilter !== 'themes' && filters.themes?.length > 0 && 
        !filters.themes.every(t => hotel.themes?.map(x => x.name).includes(t))) {
      return false;
    }

    return true;
  };

  // Generate hotel suggestions based on search input
  const hotelSuggestions = useMemo(() => {
    if (!searchValue.trim()) return [];
    
    const searchTerm = searchValue.toLowerCase().trim();
    return allHotels
      .filter(hotel => {
        // Make sure we're accessing string values and handle potential undefined values
        const hotelName = (hotel.name || '').toLowerCase();
        const cityName = (hotel.city_name || '').toLowerCase();
        return hotelName.includes(searchTerm) || cityName.includes(searchTerm);
      })
      .slice(0, 8) // Limit to 8 suggestions
      .map(hotel => ({
        id: hotel.id || '',
        name: hotel.name || '',
        city: hotel.city_name || ''
      }));
  }, [searchValue, allHotels]);

  // Handle clicks outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current && 
        !searchInputRef.current.contains(event.target) &&
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Enhanced styling for form inputs to make them more readable and visually appealing
  const roundInputStyle = {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    accentColor: "transparent",
    cursor: "pointer",
    border: "2px solid #E5E7EB",
    backgroundColor: "#fff",
    margin: "0 12px 0 0",
    transition: "all 0.2s ease-in-out",
    outline: "none",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    position: "relative",
    flexShrink: 0
  };

  // Enhanced checkbox styling
  const checkboxStyle = {
    ...roundInputStyle,
    borderRadius: "4px", // Square for checkboxes
  };

  // Style for form check containers
  const formCheckStyle = {
    padding: "12px 16px",
    marginBottom: "8px",
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
    transition: "all 0.2s ease-in-out",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    boxSizing: "border-box"
  };

  // Label styling
  const labelStyle = {
    fontSize: "0.95rem",
    fontWeight: "500",
    color: "#003663",
    cursor: "pointer",
    lineHeight: "1.4",
    margin: 0,
    flex: 1,
    textAlign: "left",
    wordWrap: "break-word",
    overflow: "hidden"
  };

  // Calculate counts for each filter option
  const filterCounts = useMemo(() => {
    const counts = {
      starRating: {},
      distance: {},
      mealPlan: {},
      facilities: {},
      themes: {}
    };

    allHotels.forEach(hotel => {
      // Only count if hotel matches current filters (excluding the one being counted)
      
      // Star ratings
      [5, 4, 3, 2, 1].forEach(stars => {
        if (matchesCurrentFilters(hotel, 'starRating') && Math.round(hotel.stars || 0) >= stars) {
          counts.starRating[stars] = (counts.starRating[stars] || 0) + 1;
        }
      });

      // Distance
      [1, 3, 5].forEach(km => {
        if (matchesCurrentFilters(hotel, 'distance') && hotel.distanceFromCenter <= km) {
          counts.distance[km] = (counts.distance[km] || 0) + 1;
        }
      });

      // Meal plans
      if (hotel.boardGroups) {
        hotel.boardGroups.forEach(bg => {
          if (matchesCurrentFilters(hotel, 'mealPlan')) {
            counts.mealPlan[bg.name] = (counts.mealPlan[bg.name] || 0) + 1;
          }
        });
      }

      // Facilities
      if (hotel.facilities) {
        hotel.facilities.forEach(f => {
          if (matchesCurrentFilters(hotel, 'facilities')) {
            counts.facilities[f.name] = (counts.facilities[f.name] || 0) + 1;
          }
        });
      }

      // Themes
      if (hotel.themes) {
        hotel.themes.forEach(t => {
          if (matchesCurrentFilters(hotel, 'themes')) {
            counts.themes[t.name] = (counts.themes[t.name] || 0) + 1;
          }
        });
      }
    });

    return counts;
  }, [allHotels, filters]);

  // Toggle star rating (checkbox style)
  const handleStarRatingChange = (value) => {
    const updated = selectedStarRatings.includes(value)
      ? selectedStarRatings.filter((v) => v !== value)
      : [...selectedStarRatings, value];
    setSelectedStarRatings(updated);
    onChangeFilters({ ...filters, starRating: updated });
  };

  // Handle hotel name search with real-time filtering
  const handleHotelNameSearch = (event) => {
    const value = event.target.value || '';
    setSearchValue(value);
    setShowSuggestions(value.trim().length > 0);
    onChangeFilters({ ...filters, hotelName: value });
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (hotelName) => {
    if (typeof hotelName !== 'string') {
      console.warn('Invalid hotel name:', hotelName);
      return;
    }
    
    setSearchValue(hotelName);
    setShowSuggestions(false);
    onChangeFilters({ ...filters, hotelName: hotelName });
    
    // Close mobile drawer if on mobile
    if (isMobile && onCloseDrawer) {
      setTimeout(() => {
        onCloseDrawer();
      }, 100);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (searchValue.trim().length > 0) {
      setShowSuggestions(true);
    }
  };

  // Handle price range change
  const handlePriceRangeChange = (event, newValue) => {
    setPriceRange(newValue);
    onChangeFilters({
      ...filters,
      priceMin: newValue[0],
      priceMax: newValue[1]
    });
  };

  // Handle distance change
  const handleDistanceChange = (km) => {
    if (km == null) {
      onChangeFilters({ ...filters, distanceKm: [] });
      return;
    }
    const current = filters.distanceKm || [];
    const updated = current.includes(km)
      ? current.filter((d) => d !== km)
      : [...current, km];
    onChangeFilters({ ...filters, distanceKm: updated });
  };

  // Toggle logic for meal plans (multi-select checkboxes)
  const handleMealPlanChange = (meal) => {
    if (meal == null) {
      onChangeFilters({ ...filters, mealPlan: [] });
      return;
    }
    const current = filters.mealPlan || [];
    const updated = current.includes(meal)
      ? current.filter((m) => m !== meal)
      : [...current, meal];
    onChangeFilters({ ...filters, mealPlan: updated });
  };

  // Toggle facility (checkbox style)
  const handleFacilityChange = (facilityName) => {
    const current = filters.facilities || [];
    const updated = current.includes(facilityName)
      ? current.filter((f) => f !== facilityName)
      : [...current, facilityName];
    onChangeFilters({ ...filters, facilities: updated });
  };

  // Toggle theme (checkbox style)
  const handleThemeChange = (themeName) => {
    const current = filters.themes || [];
    const updated = current.includes(themeName)
      ? current.filter((t) => t !== themeName)
      : [...current, themeName];
    onChangeFilters({ ...filters, themes: updated });
  };

  const clearAllFilters = () => {
    setSelectedStarRatings([]);
    setPriceRange([0, Math.max(...allHotels.map(h => h.displayPrice || 0))]);
    onChangeFilters({
      starRating: [],
      distanceKm: [],
      mealPlan: [],
      priceMin: null,
      priceMax: null,
      facilities: [],
      themes: [],
      hotelName: "",
    });
  };

  // Unique meal plans – case-insensitive dedupe, no special aliases
  const uniqueMealPlans = useMemo(() => {
    const nameMap = new Map();
    allHotels.forEach((hotel) => {
      (hotel.boardGroups || []).forEach((bg) => {
        const key = bg.name?.trim().toLowerCase();
        if (key && !nameMap.has(key)) {
          nameMap.set(key, bg.name.trim());
        }
      });
    });
    return Array.from(nameMap.values());
  }, [allHotels]);

  const uniqueFacilities = Array.from(
    new Set(allHotels.flatMap((hotel) => hotel.facilities || []).map((f) => f.name))
  );
  const uniqueThemes = Array.from(
    new Set(allHotels.flatMap((hotel) => hotel.themes || []).map((t) => t.name))
  );

  // Gold star color for star ratings
  const goldStarStyle = {
    color: "#FFC107",
    marginRight: "3px",
    fontSize: "18px",
  };

  // Style for the count badge
  const countBadgeStyle = {
    marginLeft: 'auto',
    backgroundColor: '#f0f0f0',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    color: '#666',
    minWidth: '40px',
    textAlign: 'center',
    flexShrink: 0,
    alignSelf: 'center'
  };

  // Calculate min and max prices for the slider
  const minPrice = Math.min(...allHotels.map(h => h.displayPrice || 0));
  const maxPrice = Math.max(...allHotels.map(h => h.displayPrice || 0));

  return (
    <div 
      className={isMobile ? "mobile-filter-container" : ""}
      style={{ 
        fontSize: "0.95rem", 
        ...textColorStyle,
        ...(isMobile ? { padding: '16px', background: 'white', width: '100%', boxSizing: 'border-box' } : {})
      }}
    >
      {/* Custom CSS for radio and checkbox styling */}
      <style>{`
        input[type="radio"] {
          position: relative;
        }
        
        input[type="radio"]:checked::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #003663;
        }
        
        input[type="checkbox"] {
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border: 2px solid #E5E7EB;
          border-radius: 4px;
          background: transparent;
          cursor: pointer;
          position: relative;
          transition: border-color 0.2s ease, background-color 0.2s ease;
        }
        
        input[type="checkbox"]:checked {
          border-color: transparent;
          background: transparent;
        }
        
        input[type="checkbox"]:checked::after {
          content: '✓';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #003663;
          font-size: 14px;
          font-weight: bold;
          line-height: 1;
        }
        
        input[type="radio"]:hover,
        input[type="checkbox"]:hover {
          border-color: #003663;
          box-shadow: 0 0 0 2px rgba(0, 54, 99, 0.1);
        }
      `}</style>
          <h5 className="mb-2 mt-4" style={textColorStyle}>{translation("hotel_name")}</h5>
          <div className="mb-3">
            <HotelSearchContainer ref={searchInputRef}>
              <SearchIconWrapper>
                <SearchIcon fontSize="small" />
              </SearchIconWrapper>
              <HotelNameInput
                type="text"
                placeholder={translation("search_by_hotel_name")}
                value={searchValue}
                onChange={handleHotelNameSearch}
                onFocus={handleInputFocus}
              />
              {showSuggestions && hotelSuggestions.length > 0 && (
                <SuggestionsDropdown ref={suggestionsRef}>
                  {hotelSuggestions.map((hotel) => (
                    <SuggestionItem
                      key={hotel.id || Math.random()}
                      onClick={() => handleSuggestionSelect(hotel.name)}
                    >
                      <SleepingSVG className="hotel-icon" style={{ width: '24px', height: '24px' }} />
                      <div>
                        <div className="hotel-name">{hotel.name}</div>
                        {hotel.city && <div className="hotel-location">{hotel.city}</div>}
                      </div>
                    </SuggestionItem>
                  ))}
                </SuggestionsDropdown>
              )}
            </HotelSearchContainer>
          </div>
      <h5 className="mb-2 mt-3" style={textColorStyle}>{translation("price_range")} (€)</h5>
      <div style={{
        padding: "16px 20px",
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
        border: "1px solid #E5E7EB",
        marginBottom: "16px"
      }}>
        <Slider
          value={priceRange}
          onChange={handlePriceRangeChange}
          valueLabelDisplay="auto"
          min={minPrice}
          max={maxPrice}
          sx={{
            color: '#003663',
            height: 8,
            '& .MuiSlider-thumb': {
              backgroundColor: '#FBD215',
              border: '3px solid #003663',
              width: 24,
              height: 24,
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(251, 210, 21, 0.16)',
              },
              '&::before': {
                display: 'none',
              },
            },
            '& .MuiSlider-rail': {
              backgroundColor: '#E5E7EB',
              height: 8,
              opacity: 1,
            },
            '& .MuiSlider-track': {
              backgroundColor: '#003663',
              height: 8,
              border: 'none',
            },
            '& .MuiSlider-valueLabel': {
              backgroundColor: '#003663',
              color: '#FBD215',
              fontWeight: '600',
              fontSize: '0.9rem',
              '&::before': {
                borderTopColor: '#003663',
              },
            },
          }}
        />
        <div className="d-flex justify-content-between mt-3">
          <span style={{ 
            fontSize: '1rem', 
            color: '#003663', 
            fontWeight: '600',
            backgroundColor: '#ECF2FF',
            padding: '4px 12px',
            borderRadius: '6px'
          }}>
            €{priceRange[0].toFixed(0)}
          </span>
          <span style={{ 
            fontSize: '1rem', 
            color: '#003663', 
            fontWeight: '600',
            backgroundColor: '#ECF2FF',
            padding: '4px 12px',
            borderRadius: '6px'
          }}>
            €{priceRange[1].toFixed(0)}
          </span>
        </div>
      </div>

      <h5 className="mb-2 mt-4" style={textColorStyle}>{translation("star_rating")}</h5>
      <div className="d-flex flex-column gap-2 mb-3">
        {[5, 4, 3, 2, 1].map((val) => (
          <div 
            key={val} 
            className={isMobile ? "mobile-filter-item" : ""}
            style={{
              ...(isMobile ? {} : formCheckStyle),
              borderColor: selectedStarRatings.includes(val) ? "#3B71FE" : "#E5E7EB"
            }}
            onClick={() => handleStarRatingChange(val)}
          >
            <input
              type="checkbox"
              id={`star-${val}`}
              style={isMobile ? {} : checkboxStyle}
              checked={selectedStarRatings.includes(val)}
              onChange={() => handleStarRatingChange(val)}
            />
            <label htmlFor={`star-${val}`} style={isMobile ? {} : labelStyle}>
              <div className={isMobile ? "mobile-star-container" : ""} style={isMobile ? {} : { display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {Array.from({ length: val }).map((_, i) => (
                    <StarIcon 
                      key={i} 
                      className={isMobile ? "mobile-star-icon" : ""}
                      style={isMobile ? {} : goldStarStyle} 
                    />
                  ))}
                </div>
                <span style={{ 
                  fontWeight: selectedStarRatings.includes(val) ? "600" : "400",
                  marginLeft: isMobile ? "8px" : "0"
                }}>
                  {val}+
                </span>
              </div>
            </label>
            <span 
              className={isMobile ? "count-badge" : ""}
              style={isMobile ? {} : countBadgeStyle}
            >
              {filterCounts.starRating[val] || 0}
            </span>
          </div>
        ))}
      </div>

      <h5 className="mb-2 mt-4" style={textColorStyle}>{translation("board_type")}</h5>
      <div 
        className={isMobile ? "mobile-filter-item" : ""}
        style={{
          ...(isMobile ? {} : formCheckStyle),
          borderColor: !(Array.isArray(filters.mealPlan) && filters.mealPlan.length) ? "#3B71FE" : "#E5E7EB"
        }}
      >
        <input
          type="checkbox"
          id="meal-any"
          style={isMobile ? {} : roundInputStyle}
          name="mealPlan"
          checked={!(Array.isArray(filters.mealPlan) && filters.mealPlan.length)}
          onChange={() => handleMealPlanChange(null)}
        />
        <label htmlFor="meal-any" style={isMobile ? {} : labelStyle}>
          {translation("any_board_type")}
        </label>
        <span 
          className={isMobile ? "count-badge" : ""}
          style={isMobile ? {} : countBadgeStyle}
        >
          {allHotels.filter(h => matchesCurrentFilters(h, 'mealPlan')).length}
        </span>
      </div>
      {uniqueMealPlans.map((meal) => {
        const isChecked = Array.isArray(filters.mealPlan) && filters.mealPlan.includes(meal);
        
        return (
          <div 
            key={meal} 
            className={isMobile ? "mobile-filter-item" : ""}
            style={{
              ...(isMobile ? {} : formCheckStyle),
              borderColor: isChecked ? "#3B71FE" : "#E5E7EB"
            }}
          >
            <input
              type="checkbox"
              id={`meal-${meal}`}
              style={isMobile ? {} : roundInputStyle}
              name="mealPlan"
              checked={isChecked}
              onChange={() => handleMealPlanChange(meal)}
            />
            <label htmlFor={`meal-${meal}`} style={isMobile ? {} : labelStyle}>
              {translateBoardName(meal)}
            </label>
            <span 
              className={isMobile ? "count-badge" : ""}
              style={isMobile ? {} : countBadgeStyle}
            >
              {filterCounts.mealPlan[meal] || 0}
            </span>
          </div>
        );
      })}

      <h5 className="mb-2 mt-4" style={textColorStyle}>{translation("distance_from_city_center")}</h5>
      <div 
        className={isMobile ? "mobile-filter-item" : ""}
        style={{
          ...(isMobile ? {} : formCheckStyle),
          borderColor: !(Array.isArray(filters.distanceKm) && filters.distanceKm.length) ? "#3B71FE" : "#E5E7EB"
        }}
      >
        <input
          type="checkbox"
          id="dist-any"
          style={isMobile ? {} : roundInputStyle}
          name="distanceKm"
          checked={!(Array.isArray(filters.distanceKm) && filters.distanceKm.length)}
          onChange={() => handleDistanceChange(null)}
        />
        <label htmlFor="dist-any" style={isMobile ? {} : labelStyle}>
          {translation("any_distance")}
        </label>
        <span 
          className={isMobile ? "count-badge" : ""}
          style={isMobile ? {} : countBadgeStyle}
        >
          {allHotels.filter(h => matchesCurrentFilters(h, 'distance')).length}
        </span>
      </div>
      {[1, 3, 5].map((km) => (
        <div 
          key={km} 
          className={isMobile ? "mobile-filter-item" : ""}
          style={{
            ...(isMobile ? {} : formCheckStyle),
            borderColor: (Array.isArray(filters.distanceKm) && filters.distanceKm.includes(km)) ? "#3B71FE" : "#E5E7EB"
          }}
        >
          <input
            type="checkbox"
            id={`dist-${km}`}
            style={isMobile ? {} : roundInputStyle}
            name="distanceKm"
            checked={Array.isArray(filters.distanceKm) && filters.distanceKm.includes(km)}
            onChange={() => handleDistanceChange(km)}
          />
          <label htmlFor={`dist-${km}`} style={isMobile ? {} : labelStyle}>
            {translation(`less_than_${km}_km`)}
          </label>
          <span 
            className={isMobile ? "count-badge" : ""}
            style={isMobile ? {} : countBadgeStyle}
          >
            {filterCounts.distance[km] || 0}
          </span>
        </div>
      ))}

      <h5 className="mb-2 mt-4" style={textColorStyle}>{translation("facilities")}</h5>
      {(showAllFacilities ? uniqueFacilities : uniqueFacilities.slice(0, 5)).map((facility) => (
        <div 
          key={facility} 
          className={isMobile ? "mobile-filter-item" : ""}
          style={{
            ...(isMobile ? {} : formCheckStyle),
            borderColor: filters.facilities?.includes(facility) ? "#3B71FE" : "#E5E7EB"
          }}
          onClick={() => handleFacilityChange(facility)}
        >
          <input
            type="checkbox"
            id={`fac-${facility}`}
            style={isMobile ? {} : checkboxStyle}
            checked={filters.facilities?.includes(facility)}
            onChange={() => handleFacilityChange(facility)}
          />
          <label htmlFor={`fac-${facility}`} style={isMobile ? {} : labelStyle}>
            {translateFacilityName(facility)}
          </label>
          <span 
            className={isMobile ? "count-badge" : ""}
            style={isMobile ? {} : countBadgeStyle}
          >
            {filterCounts.facilities[facility] || 0}
          </span>
        </div>
      ))}
      {uniqueFacilities.length > 5 && (
        <button
          onClick={() => setShowAllFacilities(!showAllFacilities)}
          className="btn btn-link px-0 mt-1"
          style={{ fontWeight: 500, color: "#003663" }}
        >
          {showAllFacilities ? (
            <>
              {translation("show_less")} <ExpandLessIcon fontSize="small" className="ms-1" />
            </>
          ) : (
            <>
              {translation("show_more")} <ExpandMoreIcon fontSize="small" className="ms-1" />
            </>
          )}
        </button>
      )}

      {/* Hotel Themes */}
      {uniqueThemes.length > 0 && (
        <>
          <h5 className="mb-2 mt-4" style={textColorStyle}>{translation("hotel_themes")}</h5>
          {(showAllThemes ? uniqueThemes : uniqueThemes.slice(0, 5)).map((theme) => (
            <div 
              key={theme} 
              className={isMobile ? "mobile-filter-item" : ""}
              style={{
                ...(isMobile ? {} : formCheckStyle),
                borderColor: filters.themes?.includes(theme) ? "#3B71FE" : "#E5E7EB"
              }}
              onClick={() => handleThemeChange(theme)}
            >
              <input
                type="checkbox"
                id={`theme-${theme}`}
                style={isMobile ? {} : checkboxStyle}
                checked={filters.themes?.includes(theme)}
                onChange={() => handleThemeChange(theme)}
              />
              <label htmlFor={`theme-${theme}`} style={isMobile ? {} : labelStyle}>
                {translateThemeName(theme)}
              </label>
              <span 
                className={isMobile ? "count-badge" : ""}
                style={isMobile ? {} : countBadgeStyle}
              >
                {filterCounts.themes[theme] || 0}
              </span>
            </div>
          ))}
          {uniqueThemes.length > 5 && (
            <button
              onClick={() => setShowAllThemes(!showAllThemes)}
              className="btn btn-link px-0 mt-1"
              style={{ fontWeight: 500, color: "#003663" }}
            >
              {showAllThemes ? (
                <>
                  {translation("show_less")} <ExpandLessIcon fontSize="small" className="ms-1" />
                </>
              ) : (
                <>
                  {translation("show_more")} <ExpandMoreIcon fontSize="small" className="ms-1" />
                </>
              )}
            </button>
          )}
        </>
      )}

      <div className="mt-4" style={{ marginBottom: 40 }}>
        <button
          onClick={clearAllFilters}
          className={isMobile ? "mobile-clear-button" : ""}
          style={isMobile ? {} : {
            borderRadius: "16px",
            boxShadow: "rgba(0, 0, 0, 0.25) 0px 6px 12px",
            fontWeight: 600,
            fontSize: "1rem",
            color: "#003663",
            backgroundColor: "#FBD215",
            border: "none",
            width: "100%",
            padding: "12px 0",
            cursor: "pointer",
            transition: "all 0.3s ease",
          }}
          onMouseOver={isMobile ? undefined : (e) => {
            e.currentTarget.style.transform = "scale(1.02)";
            e.currentTarget.style.boxShadow = "rgba(0, 0, 0, 0.35) 0px 8px 16px";
          }}
          onMouseOut={isMobile ? undefined : (e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "rgba(0, 0, 0, 0.25) 0px 6px 12px";
          }}
        >
          {translation("clear_all_filters")}
        </button>
      </div>
    </div>
  );
}

export default FiltersSidebar;
